import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Zap, FileText, Layers, Rocket, MessagesSquare, ShieldCheck, ChevronDown, Clock } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import SepaMandateModal from "@/components/SepaMandateModal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Seo, { faqJsonLd } from "@/components/Seo";
import { toast } from "sonner";

const fmtEur = (v) => v.toFixed(2).replace(".", ",");
const creditUnit = (q) => (q >= 20 ? 2.4995 : q >= 10 ? 2.999 : 5.99);
const creditTotal = (q) => Math.round(creditUnit(q) * q * 100) / 100;

const OFFER_END = new Date(2026, 10, 29, 23, 59, 59).getTime();
const pad2 = (n) => String(n).padStart(2, "0");

function OfferCountdown() {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = OFFER_END - now;
    if (diff <= 0) return null;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return (
        <p className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-5 py-2.5 rounded-full text-xs font-bold bg-amber-50 border border-amber-300 text-amber-800" data-testid="bundle-offer-deadline">
            <Clock size={13} /> Angebot gilt nur bis zum 29.11.2026 — noch
            <span className="font-mono text-[13px] font-extrabold text-red-700 tabular-nums" data-testid="offer-countdown">
                {d} {d === 1 ? "Tag" : "Tage"} {pad2(h)}:{pad2(m)}:{pad2(s)}
            </span>
        </p>
    );
}

const SEGMENTS = [
    { id: "einzeln", tag: "Für Eilige", title: "Ich brauche jetzt eine Bewerbung", sub: "Eine Stelle, ein kompletter Bewerbungssatz — fertig in Minuten.", arrow: "Zum Einzelkauf", icon: FileText },
    { id: "pakete", tag: "Für Vielbewerber", title: "Ich bewerbe mich auf viele Stellen", sub: "Im Paket zahlst du bis zu 58 % weniger pro Bewerbung.", arrow: "Zu den Paketen", icon: Layers },
    { id: "abo", tag: "Für Ambitionierte", title: "Ich will bis zum Gespräch begleitet werden", sub: "Laufende Unterstützung, jederzeit kündbar.", arrow: "Zum Abo", icon: Rocket },
];

const FAQ = [
    { q: "Kann ich mein Abo jederzeit kündigen?", a: "Ja. Kündigung mit einem Klick im Konto — keine Mindestlaufzeit, keine Wartezeit. Dein Zugang bleibt bis zum Ende des bezahlten Zeitraums aktiv." },
    { q: "Verfallen gekaufte Bewerbungen?", a: "Nein. Dein Bewerbungs-Guthaben (Einzelkauf oder Paket) hat kein Ablaufdatum und ist frei auf Stellen verteilbar." },
    { q: "Muss ich Autopilot oder den Interview-Coach mitbuchen?", a: "Nein. Beide sind optional: Autopilot-KI ab 9,99 €/Monat, Interview-Coach einmalig 12,99 € — oder beides inklusive im Unlimited-Abo." },
    { q: "Kann ich vorher kostenlos testen?", a: "Ja. Registriere dich kostenlos und du bist automatisch für 3 Tage freigeschaltet — 1 komplette Bewerbung als PDF-Download inklusive, ohne Kreditkarte. Word-Export und weitere Bewerbungen gibt es nach dem ersten Kauf." },
];

export default function PricingPage() {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [qty, setQty] = useState(10);
    const [customOpen, setCustomOpen] = useState(false);
    const [sepaPkg, setSepaPkg] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        api.get("/payments/packages").then(({ data }) => setPacks(data)).catch(() => setPacks([]));
    }, []);

    const startCheckout = async (id, quantity = 1) => {
        try {
            setLoading(true);
            const { data } = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin, quantity });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Checkout fehlgeschlagen");
            setLoading(false);
        }
    };

    const requireLogin = () => {
        if (!user || typeof user !== "object") {
            window.location.href = "/login?next=/preise";
            return true;
        }
        return false;
    };

    const buyCredits = (quantity) => {
        if (requireLogin()) return;
        startCheckout("credits", quantity);
    };

    const buyAddon = (id) => {
        if (requireLogin()) return;
        startCheckout(id);
    };

    const subscribe = (id) => {
        if (id === "free") {
            window.location.href = user ? "/dashboard" : "/registrieren";
            return;
        }
        if (requireLogin()) return;
        const p = packs.find((x) => x.id === id);
        setSepaPkg({ id, name: p?.name || id, price: p?.price });
    };

    const tiers = packs.filter((p) => ["basic", "premium", "business"].includes(p.id));

    return (
        <PublicLayout>
            <Seo
                title="KI-Bewerbung Preise: ab 5,99 € pro Bewerbung | Bewerbung24.ai"
                description="Transparente Preise: Einzelbewerbung 5,99 €, 10er-Paket 29,99 €, 20er-Paket 49,99 € oder Abo ab 9,99 €/Monat. Inkl. MwSt., jederzeit kündbar, kostenlos testen."
                path="/preise"
                jsonLd={faqJsonLd(FAQ)}
            />
            {/* HERO — Selbstsegmentierung */}
            <section className="relative overflow-hidden bg-white" data-testid="pricing-hero">
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(231,52,63,0.04) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-24 pb-10 text-center">
                    <span className="eyebrow-warm">Über 12.000 erstellte Bewerbungen</span>
                    <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-warm-strong leading-[0.98] max-w-3xl mx-auto">
                        Was brauchst du gerade für <span className="gradient-warm">deine Bewerbung?</span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-warm max-w-xl mx-auto">
                        Wähl aus, wo du stehst — wir zeigen dir die passende Option. Kein Abo nötig, wenn du keins willst.
                    </p>
                    <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto" data-testid="pricing-segments">
                        {SEGMENTS.map((s) => (
                            <a key={s.id} href={`#${s.id}`} data-testid={`segment-${s.id}`} className="group text-left rounded-2xl border border-zinc-200 bg-white p-5 hover:border-red-300 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-warm inline-flex items-center gap-1.5"><s.icon size={12} className="text-amber-500" /> {s.tag}</p>
                                <p className="font-heading font-bold text-warm-strong mt-2 leading-snug">{s.title}</p>
                                <p className="text-xs text-warm mt-1.5">{s.sub}</p>
                                <p className="text-xs font-bold text-red-600 mt-3 inline-flex items-center gap-1">{s.arrow} <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></p>
                            </a>
                        ))}
                    </div>
                    <div className="mt-10 flex items-center justify-center gap-2 sm:gap-3 flex-wrap text-xs text-warm" data-testid="pricing-progress-strip">
                        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Bewerbung geschrieben</span>
                        <span className="h-px w-6 bg-zinc-300" />
                        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Antwort erhalten</span>
                        <span className="h-px w-6 bg-zinc-300" />
                        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-zinc-300" /> Gespräch vorbereitet</span>
                    </div>
                </div>
            </section>

            {/* EINZELKAUF */}
            <section id="einzeln" className="bg-[#FAF8F5] border-t border-zinc-100 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
                    <div className="text-center max-w-xl mx-auto mb-8">
                        <span className="eyebrow-warm">Ohne Verpflichtung</span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-warm-strong mt-3">Erstmal ausprobieren</h2>
                        <p className="text-warm mt-2">Eine Bewerbung, ein fairer Preis. Keine Verlängerung, kein Abo.</p>
                    </div>
                    <div className="max-w-2xl mx-auto card-elev p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="pricing-single-strip">
                        <div className="flex items-center gap-4 min-w-0">
                            <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>
                                <FileText size={20} />
                            </div>
                            <div className="min-w-0">
                                <p className="font-heading font-bold text-warm-strong">1 Bewerbung</p>
                                <p className="text-xs text-warm">Anschreiben + Lebenslauf + Mappe, sofort als PDF — ohne Wasserzeichen</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className="text-2xl font-bold text-warm-strong whitespace-nowrap">5,99 €</p>
                            <button onClick={() => buyCredits(1)} disabled={loading} data-testid="single-buy-btn" className="px-5 py-2.5 rounded-full text-sm font-bold btn-primary-grad disabled:opacity-60">
                                Jetzt schreiben
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* BUNDLES */}
            <section id="pakete" className="bg-white border-t border-zinc-100 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
                    <div className="text-center max-w-xl mx-auto mb-10">
                        <span className="eyebrow-warm">Meistgewählt bei mehreren Bewerbungen</span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-warm-strong mt-3">Im Paket sparst du am meisten</h2>
                        <p className="text-warm mt-2">Wer sich auf mehrere Stellen bewirbt, zahlt pro Bewerbung deutlich weniger — echter Vergleich zum Einzelpreis von 5,99 €.</p>
                        <OfferCountdown />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="pricing-bundles">
                        {/* 10er */}
                        <div className="card-elev p-7" data-testid="bundle-10">
                            <p className="font-heading text-xl font-bold text-warm-strong">10 Bewerbungen</p>
                            <p className="text-xs font-bold text-emerald-700 mt-1">Du sparst 50 % gegenüber Einzelkauf</p>
                            <div className="mt-4 flex items-baseline gap-2.5">
                                <span className="text-sm text-warm">statt 10 × 5,99 €</span>
                                <span className="text-3xl font-bold text-warm-strong">29,99 €</span>
                            </div>
                            <p className="text-xs text-warm mt-1">entspricht 3,00 € pro Bewerbung</p>
                            <button onClick={() => buyCredits(10)} disabled={loading} data-testid="bundle-10-btn" className="mt-5 w-full px-5 py-2.5 rounded-full text-sm font-bold bg-white border-2 border-zinc-300 hover:border-red-400 text-warm-strong transition-colors">
                                Paket wählen
                            </button>
                            <ul className="mt-4 space-y-2 text-xs text-warm">
                                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-600" /> Verfällt nie — ohne Ablaufdatum</li>
                                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-600" /> Frei auf Stellen verteilbar</li>
                            </ul>
                        </div>
                        {/* 20er */}
                        <div className="card-elev p-7 relative border-2 !border-emerald-400" data-testid="bundle-20">
                            <span className="absolute -top-3 left-6 text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-500 text-white">Bestes Preis-Leistungs-Verhältnis</span>
                            <p className="font-heading text-xl font-bold text-warm-strong">20 Bewerbungen</p>
                            <p className="text-xs font-bold text-emerald-700 mt-1">Du sparst 58 % gegenüber Einzelkauf</p>
                            <div className="mt-4 flex items-baseline gap-2.5">
                                <span className="text-sm text-warm">statt 20 × 5,99 €</span>
                                <span className="text-3xl font-bold text-warm-strong">49,99 €</span>
                            </div>
                            <p className="text-xs text-warm mt-1">entspricht 2,50 € pro Bewerbung</p>
                            <button onClick={() => buyCredits(20)} disabled={loading} data-testid="bundle-20-btn" className="mt-5 w-full px-5 py-2.5 rounded-full text-sm font-bold btn-primary-grad disabled:opacity-60">
                                Paket wählen
                            </button>
                            <ul className="mt-4 space-y-2 text-xs text-warm">
                                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-600" /> Verfällt nie — ohne Ablaufdatum</li>
                                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-600" /> Frei auf Stellen verteilbar</li>
                                <li className="flex items-center gap-2"><Check size={13} className="text-emerald-600" /> Priorisierter Support</li>
                            </ul>
                        </div>
                    </div>

                    {/* Eigene Menge */}
                    <div className="max-w-3xl mx-auto mt-6 text-center">
                        <button onClick={() => setCustomOpen(!customOpen)} data-testid="custom-qty-toggle" className="text-xs font-semibold text-warm hover:text-red-600 inline-flex items-center gap-1">
                            Eigene Menge wählen <ChevronDown size={13} className={`transition-transform ${customOpen ? "rotate-180" : ""}`} />
                        </button>
                        {customOpen && (
                            <div className="mt-4 card-elev p-6 text-left" data-testid="custom-qty-panel">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <p className="text-sm font-bold text-warm-strong">{qty} {qty === 1 ? "Bewerbung" : "Bewerbungen"} — {fmtEur(creditTotal(qty))} €</p>
                                        <p className="text-[11px] text-warm mt-0.5">
                                            {qty >= 10
                                                ? `Paketpreis aktiv: ${fmtEur(creditUnit(qty))} € pro Bewerbung (statt 5,99 €)`
                                                : qty > 1
                                                    ? `Tipp: Ab 10 Stück zahlst du nur 3,00 € pro Bewerbung — 10 Stück kosten 29,99 € (du zahlst gerade ${fmtEur(creditTotal(qty))} € für ${qty})`
                                                    : "Einzelpreis 5,99 €"}
                                        </p>
                                    </div>
                                    <button onClick={() => buyCredits(qty)} disabled={loading} data-testid="credits-buy-btn" className="px-5 py-2.5 rounded-full text-sm font-bold btn-primary-grad disabled:opacity-60">
                                        {qty} für {fmtEur(creditTotal(qty))} € kaufen
                                    </button>
                                </div>
                                <input type="range" min={1} max={50} value={qty} onChange={(e) => setQty(parseInt(e.target.value))} data-testid="credits-slider" className="w-full mt-4 accent-red-600" />
                                <div className="flex justify-between text-[10px] text-warm mt-1"><span>1</span><span>10</span><span>20</span><span>50</span></div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ABOS */}
            <section id="abo" className="bg-[#FAF8F5] border-t border-zinc-100 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
                    <div className="text-center max-w-xl mx-auto mb-10">
                        <span className="eyebrow-warm">Laufende Begleitung</span>
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-warm-strong mt-3">Oder dauerhaft gut aufgestellt sein</h2>
                        <p className="text-warm mt-2">Für alle, die regelmäßig bewerben oder Wert auf Feedback legen. Jederzeit kündbar, keine Mindestlaufzeit.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto items-start" data-testid="pricing-tiers">
                        {tiers.map((p) => {
                            const reco = p.id === "premium";
                            return (
                                <div key={p.id} className={`card-elev p-7 relative ${reco ? "border-2 !border-red-400 md:-translate-y-2" : ""}`} data-testid={`tier-${p.id}`}>
                                    {reco && <span className="absolute -top-3 left-6 text-[11px] font-bold px-3 py-1 rounded-full text-white" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }}>Beliebteste Wahl</span>}
                                    <p className="font-heading text-lg font-bold text-warm-strong">{p.name}</p>
                                    <p className="text-xs text-warm mt-0.5 min-h-[2rem]">{p.tagline}</p>
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span className="text-3xl font-bold text-warm-strong">{fmtEur(p.price)} €</span>
                                        <span className="text-sm text-warm">/ Monat inkl. MwSt.</span>
                                    </div>
                                    <p className="text-[11px] text-warm mb-4">{p.limit >= 99999 ? "unbegrenzte Bewerbungen" : `bis zu ${p.limit} Bewerbungen im Monat`}</p>
                                    <ul className="space-y-2 text-[13px] text-warm-strong mb-5">
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" /> {f}</li>
                                        ))}
                                    </ul>
                                    <button onClick={() => subscribe(p.id)} disabled={loading} data-testid={`pricing-cta-${p.id}`} className={`w-full px-5 py-2.5 rounded-full text-sm font-bold transition-colors disabled:opacity-60 ${reco ? "btn-primary-grad" : "bg-white border-2 border-zinc-300 hover:border-red-400 text-warm-strong"}`}>
                                        {p.name} wählen
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-8 flex items-center justify-center gap-5 flex-wrap text-xs text-warm" data-testid="pricing-trust-line">
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-600" /> Jederzeit kündbar</span>
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-600" /> Keine Mindestlaufzeit</span>
                        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-emerald-600" /> Transparente Abrechnung inkl. MwSt.</span>
                    </div>
                </div>
            </section>

            {/* ZUSATZFEATURES */}
            <section id="zusatz" className="bg-white border-t border-zinc-100 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
                    <div className="rounded-3xl p-8 sm:p-12 text-white" style={{ background: "#1C1917" }} data-testid="pricing-addons">
                        <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-amber-400">Wächst mit deinem Erfolg</span>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-3 max-w-lg">Zusatzfeatures — genau dann, wenn du sie brauchst</h2>
                        <p className="text-sm text-stone-400 mt-2 max-w-xl">Du musst nichts vorab buchen. Diese Funktionen kannst du jederzeit einzeln dazunehmen — oder du hast sie im Unlimited-Abo bereits inklusive.</p>
                        <div className="mt-8 grid md:grid-cols-2 gap-6">
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="addon-autopilot">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Nach dem Versand</p>
                                <p className="font-heading text-lg font-bold mt-1.5 inline-flex items-center gap-2"><Rocket size={16} className="text-amber-400" /> Autopilot-KI</p>
                                <p className="text-sm text-stone-300 mt-2">Findet passende Stellen und bewirbt dich automatisch weiter, während du dich auf Antworten konzentrierst.</p>
                                <div className="mt-4 flex items-center gap-3 flex-wrap">
                                    <button onClick={() => buyAddon("addon_autopilot")} disabled={loading} data-testid="addon-autopilot-btn" className="px-4 py-2 rounded-full text-xs font-bold bg-white text-stone-900 hover:bg-amber-50 transition-colors disabled:opacity-60">
                                        Für 9,99 €/Monat zubuchen
                                    </button>
                                    <span className="text-[11px] text-amber-300">✓ In Unlimited enthalten</span>
                                </div>
                            </div>
                            <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="addon-coach">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Nach der Einladung</p>
                                <p className="font-heading text-lg font-bold mt-1.5 inline-flex items-center gap-2"><MessagesSquare size={16} className="text-amber-400" /> Interview-Coach</p>
                                <p className="text-sm text-stone-300 mt-2">Simuliert dein Vorstellungsgespräch realistisch, bewertet jede Antwort und gibt dir gezieltes Feedback vor dem echten Termin.</p>
                                <div className="mt-4 flex items-center gap-3 flex-wrap">
                                    <button onClick={() => buyAddon("addon_coach")} disabled={loading} data-testid="addon-coach-btn" className="px-4 py-2 rounded-full text-xs font-bold bg-white text-stone-900 hover:bg-amber-50 transition-colors disabled:opacity-60">
                                        Einmalig 12,99 € freischalten
                                    </button>
                                    <span className="text-[11px] text-amber-300">✓ In Unlimited enthalten</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[#FAF8F5] border-t border-zinc-100">
                <div className="max-w-3xl mx-auto px-6 lg:px-8 py-14">
                    <h2 className="font-heading text-3xl font-bold text-warm-strong text-center mb-8">Häufige Fragen</h2>
                    <div className="divide-y divide-zinc-200" data-testid="pricing-faq">
                        {FAQ.map((f, i) => (
                            <div key={i} className="py-5">
                                <p className="font-semibold text-warm-strong text-sm">{f.q}</p>
                                <p className="text-sm text-warm mt-1.5 leading-relaxed">{f.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-white border-t border-zinc-100">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-warm-strong">Bereit für deine nächste Bewerbung?</h2>
                    <p className="text-warm mt-3">Starte einzeln, im Paket oder im Abo — du entscheidest. Kostenlos testen geht auch.</p>
                    <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                        <Link to={user ? "/dashboard" : "/registrieren"} data-testid="pricing-final-cta" className="px-8 py-3.5 rounded-full text-sm font-bold btn-primary-grad inline-flex items-center gap-2">
                            <Sparkles size={15} /> Jetzt kostenlos testen
                        </Link>
                        <a href="#pakete" className="px-8 py-3.5 rounded-full text-sm font-bold bg-white border-2 border-zinc-300 hover:border-red-400 text-warm-strong transition-colors inline-flex items-center gap-2">
                            <Zap size={15} className="text-amber-500" /> Pakete ansehen
                        </a>
                    </div>
                </div>
            </section>

            <SepaMandateModal
                open={!!sepaPkg}
                pkg={sepaPkg}
                onClose={() => setSepaPkg(null)}
                onSigned={() => {
                    const p = sepaPkg;
                    setSepaPkg(null);
                    startCheckout(p.id);
                }}
            />
        </PublicLayout>
    );
}
